import type { BadgeProps } from "@/v2-ui/badge"
import type { DotProps } from "@/v2-backoffice-client/components/dot"

/**
 * The create flows' CRM vocabulary, shared by the customer, user and
 * invitation forms: where a duplicate candidate lives, why it matched, and
 * how a create's HubSpot step ended. Typed as string literals (never the
 * generated enums) so nothing imports the schema at runtime — the same
 * bridge invitation.constants uses for email templates.
 */

export const MATCH_SOURCE_BACKOFFICE = "BACKOFFICE"
export const MATCH_SOURCE_HUBSPOT = "HUBSPOT"
export type MatchSourceLiteral =
  | typeof MATCH_SOURCE_BACKOFFICE
  | typeof MATCH_SOURCE_HUBSPOT

export const MATCH_KIND_ORG_NUMBER = "ORG_NUMBER"
export const MATCH_KIND_DOMAIN = "DOMAIN"
export const MATCH_KIND_EMAIL = "EMAIL"
export const MATCH_KIND_NAME = "NAME"
export type MatchKindLiteral =
  | typeof MATCH_KIND_ORG_NUMBER
  | typeof MATCH_KIND_DOMAIN
  | typeof MATCH_KIND_EMAIL
  | typeof MATCH_KIND_NAME

export const CRM_STATUS_LINKED = "LINKED"
export const CRM_STATUS_CREATED = "CREATED"
export const CRM_STATUS_SKIPPED = "SKIPPED"
export const CRM_STATUS_FAILED = "FAILED"
export type CrmStatusLiteral =
  | typeof CRM_STATUS_LINKED
  | typeof CRM_STATUS_CREATED
  | typeof CRM_STATUS_SKIPPED
  | typeof CRM_STATUS_FAILED

/** The one-line outcome shape every create mutation reports. */
export type CrmOutcome = {
  Status: CrmStatusLiteral
  ExternalId?: string | null
  Message?: string | null
}

/** What the badge on a candidate says about why it is in the list. */
export const MATCH_KIND_LABEL: Record<MatchKindLiteral, string> = {
  [MATCH_KIND_ORG_NUMBER]: "Same org.nr",
  [MATCH_KIND_DOMAIN]: "Same website",
  [MATCH_KIND_EMAIL]: "Same email",
  [MATCH_KIND_NAME]: "Similar name",
}

/**
 * Exact facts (org.nr, email) are a verdict — in Backoffice they mean "this
 * IS a duplicate", in HubSpot "this IS the record to link". Softer facts
 * (domain, name) are hints and read as such.
 */
export function getMatchBadgeVariant(
  source: MatchSourceLiteral,
  kind: MatchKindLiteral,
): BadgeProps["variant"] {
  const exact = kind === MATCH_KIND_ORG_NUMBER || kind === MATCH_KIND_EMAIL
  if (source === MATCH_SOURCE_BACKOFFICE) {
    return exact ? "destructive" : "warning"
  }
  return exact ? "success" : "secondary"
}

/**
 * The same verdict as a status dot (the list rows' "where it lives" line):
 * red = a Backoffice duplicate on an exact fact, amber = a Backoffice
 * lookalike, green = the HubSpot record to link (exact fact), blue = a
 * HubSpot lookalike. Mirrors the status-line and bulk-table dots.
 */
export function getMatchDotVariant(
  source: MatchSourceLiteral,
  kind: MatchKindLiteral,
): DotProps["variant"] {
  const exact = kind === MATCH_KIND_ORG_NUMBER || kind === MATCH_KIND_EMAIL
  if (source === MATCH_SOURCE_BACKOFFICE) {
    return exact ? "destructive" : "warning"
  }
  return exact ? "success-1" : "info"
}

/** The debounce between the operator's last keystroke and a lookup. */
export const CRM_LOOKUP_DEBOUNCE_MS = 350

/** How many candidates a form shows — the real duplicate and its lookalikes. */
export const CRM_LOOKUP_LIMIT = 6

/** Backend thresholds mirrored client-side so nothing useless is requested. */
export const MIN_ORG_NUMBER_DIGITS = 9
export const MIN_NAME_TOKEN_LENGTH = 3
export const MIN_PERSON_NAME_LENGTH = 2
